import { Injectable } from '@angular/core';
import { Variant } from '../models/variant.model';
import { EditorObject } from '../models/editor-object.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { Command } from '../models/commands/command.model';
import { InteriorObject } from '../models/interior-object.model';
import { ComponentService } from './component.service';
import { EditCommand } from '../models/commands/editCommand.model';
import { mapInteriorObjectFromEditorObject } from '../helper/mapInteriorObjectFromEditorObject';
import { CloneService } from './clone.service';
import { getComponentFromSource } from '../generics-helper/getComponentFromSource.generic';
import { IdentificationComponent } from '../models/components/commonComponents/IdentificationComponent';
import { ComponentNames } from '../helper/componentNames';
import { PropertyWindowValidationService } from './propertyWindowValidation.service';
import { BaseComponent } from '../models/components/BaseComponent';
import { FloorEnum } from '../models/enums/FloorEnum';
import { LayerEnum } from '../models/enums/LayerEnum';
import { BaseDimensionComponent } from '../models/components/commonComponents/BaseDimensionComponent';
import { mapStepsToDegree } from '../helper/mapStepToDegree';
import { getDoorIdentifierNames } from '../helper/getDoorIdentifierNames';
import { changeVariantFloorStructureToEditorVariantFloorStructure as changeVariantFloorStructureToEditorVariantFloorStructure } from '../helper/changeVariantFloorStructureToEditorVariantFloorStructure';
import { IFloor } from '../models/interfaces/iFloor.interface';
import { DoorComponent } from '../models/components/objectComponents/DoorComponent';

/**
 * Handles communictaion between editorcanvas, editor, Toolbox and Properties Window
 * Holds information about Wagon, Variant
 */

@Injectable({ providedIn: 'root' })
export class EditorService {
  currentVariant: Variant;
  isNewObj: boolean;
  private projectName: string;
  private wagonName: string;
  private companyName: string;
  public currentFloor: BehaviorSubject<number> = new BehaviorSubject(null);
  public currentLayer: BehaviorSubject<number> = new BehaviorSubject(null);
  public currentSnapingMode: BehaviorSubject<number> = new BehaviorSubject(null);
  public measurementFunctionState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public snappingFunctionState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public freeDrawingFunctionState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private currentObjectBehavior: BehaviorSubject<EditorObject> = new BehaviorSubject(null);
  private currentCommandBehavior: BehaviorSubject<Command> = new BehaviorSubject<Command>(null);
  private floorContainers: any[];

  constructor(private componentService: ComponentService, private cloneService: CloneService) {
    this.componentService.setValidationService(new PropertyWindowValidationService());
    this.componentService.componentsChanged.subscribe(() => {
      var editCommand = new EditCommand(this.currentObjectBehavior.value, this.componentService.getCurrentComponents());
      this.setCommand(editCommand);
    });
  }

  debugLog() {
    console.log(this.getEditorObjects());
  }

  setMeasurementFunctionState(state: boolean) {
    this.measurementFunctionState.next(state);
  }

  setSnappingFunctionState(state: boolean) {
    this.snappingFunctionState.next(state);
  }

  setFreeDrawingFunctionState(state: boolean) {
    this.freeDrawingFunctionState.next(state);
  }

  setCurrentVariant(variant: Variant) {
    let result = changeVariantFloorStructureToEditorVariantFloorStructure(variant, this.cloneService);
    this.currentVariant = result.parsedVariant;
    this.floorContainers = result.floorContainers;
  }

  initFloorLayerAndSnapingMode() {
    if (this.currentFloor.value == null && this.currentLayer.value == null && this.currentSnapingMode.value == null) {
      this.setCurrentFloor(FloorEnum.First);
      this.setCurrentLayer(LayerEnum.ground);
      this.setCurrentSnapingMode(0);
    }
    if (this.currentVariant.floors.length === 1 && this.currentFloor.value === 2) {
      this.setCurrentFloor(FloorEnum.First);
    }
  }

  getFloorContainers(floor: number) {
    return this.floorContainers.filter(x => x.floor === floor);
  }

  //can be subscribed
  getCurrentEditorObjectObservable(): Observable<EditorObject> {
    return this.currentObjectBehavior.asObservable();
  }

  getCurrentEditorObject(): EditorObject {
    return this.currentObjectBehavior.value;
  }

  setCurrentEditorObjectFromCanvas(val: EditorObject): void {
    this.isNewObj = false;
    this.prepareAdditionalDataForValidation(val);
    this.setCurrentEditorObject(val);
    this.emitNewComponentsValue(val);
  }

  resetCurrentEditorObjectFromCanvas() {
    this.isNewObj = false;
    this.setCurrentEditorObject(null);
    this.setComponentServiceValue(null);
    this.clearAdditionalData();
  }

  clearAdditionalData() {
    this.componentService.getValidationService().setAdditionalDataForValidation(null);
  }

  prepareAdditionalDataForValidation(val: EditorObject) {
    let idComp = getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, val.components);
    // check if it is necessary to prepare door identifiers for the validation service
    if (idComp == null) {
      return;
    }
    else {
      let allDoors = this.getDoorsAsEditorObjects(this.currentVariant.floors);
      let dooridentifiernames = getDoorIdentifierNames(allDoors, val);
      this.componentService.getValidationService().setAdditionalDataForValidation(dooridentifiernames);
    }
  }

  getDoorsAsEditorObjects(floors) {
    let eObjects: EditorObject[] = new Array();
    for (let index = 0; index < floors.length; index++) {
      const floor: IFloor = floors[index];

      for (let index = 0; index < floor.elements.length; index++) {
        const editorObject: EditorObject = (floor.elements[index] as EditorObject);
        let door = getComponentFromSource<DoorComponent>(ComponentNames.doorComponent, editorObject.components);
        if (door !== null) {
          let idComp = getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, editorObject.components);
          if (idComp !== null) {
            eObjects.push(editorObject);
          }

        }
      }

      return eObjects;
    }
  }

  emitNewComponentsValue(val: EditorObject) {
    const comp = mapInteriorObjectFromEditorObject(val).components;
    this.setComponentServiceValue(comp);
  }

  setComponentServiceValue(val: BaseComponent[]) {
    this.componentService.setCurrentComponents(val);
  }

  setCurrentEditorObject(val: EditorObject): void {
    this.currentObjectBehavior.next(val);
  }


  getCommand(): Observable<Command> {
    return this.currentCommandBehavior.asObservable();
  }

  setCommand(val: Command): void {
    this.currentCommandBehavior.next(val);
  }

  //creates new editorObject -> isNewObj = true
  setCurrentEditorObjectFromInteriorObject(intO: InteriorObject): void {
    this.isNewObj = true;

    // convert steps into degree
    var baseDimension = getComponentFromSource<BaseDimensionComponent>(ComponentNames.baseDimensionsComponent, intO.components);
    baseDimension.rotation = mapStepsToDegree(baseDimension.rotation);

    let ed = new EditorObject(intO);
    let entrancedoor = getComponentFromSource(ComponentNames.entranceDoorComponent, ed.components);
    if (entrancedoor != null) {
      let idComp = getComponentFromSource<IdentificationComponent>(ComponentNames.identificationComponent, ed.components);
      idComp.userId = this.getDefaultDoorName();
    }
    this.setCurrentEditorObject(ed);
  }

  getDefaultDoorName(): string {
    return "Door " + this.createSmallGuid();
  }

  createSmallGuid(): string {
    return 'xxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0,
        v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    }).toUpperCase();
  }

  removeObjectFromVariant(id: number) {
    var arr = this.currentVariant.floors[this.currentFloor.value].elements as Array<EditorObject>;
    for (var i = 0; i < arr.length; i++) {
      if (arr[i]._id === id) {
        arr.splice(i, 1);
      }
    }
  }

  addObjectToVariant(obj: EditorObject) {
    let currentFloor = this.currentFloor.value;
    let floor = this.currentVariant.floors.find(f => f.floorType === currentFloor);
    floor.elements.push(obj);
  }

  getEditorObjects(): Array<EditorObject> {
    let currentFloor = this.currentFloor.value;
    let floor = this.currentVariant.floors.find(f => f.floorType === currentFloor);
    return floor.elements as Array<EditorObject>;
  }

  public getScreenshotDescription(floorName: string, layerName: string): string {
    const seperator = ' / '

    var project = this.projectName;
    var wagon = this.wagonName;
    var layout = this.currentVariant.name;
    var createdAtLabel = 'created on: ' + new Date().toLocaleString();
    var company = 'by: ' + this.companyName;
    var description = project + seperator + wagon + seperator + layout + seperator + floorName + seperator + layerName + seperator + createdAtLabel + seperator + company;
    return description;
  }

  public setLayoutDescription(projectName: string, wagonName: string, companyName: string) {
    this.projectName = projectName;
    this.wagonName = wagonName;
    this.companyName = companyName;
  }

  public setCurrentFloor(val: number) {
    this.currentFloor.next(val);
  }

  public getCurrentFloor(): number {
    return this.currentFloor.value;
  }

  public getCurrentLayer(): number {
    return this.currentLayer.value;
  }

  public setCurrentLayer(val: number) {
    this.currentLayer.next(val);
  }
  public setCurrentSnapingMode(val: number) {
    this.currentSnapingMode.next(val);
  }

  public isTwoFloorVehicle(): boolean {
    return this.currentVariant.floors.length == 2;
  }

  resetBehaviorSubjectValues() {
    this.setCommand(null);
    this.setCurrentEditorObject(null);
    this.setCurrentFloor(null);
    this.setCurrentLayer(null);
    this.setCurrentSnapingMode(null);
    this.currentVariant = null;
    this.componentService.setCurrentComponents(null);
  }
}